import type { CSSProperties } from 'react';

import {
  TILE_RADIUS_REM,
  TILE_SUBTITLE_FONT_SIZE,
  TILE_TITLE_FONT_SIZE,
  TILE_TITLE_FONT_WEIGHT,
  rem,
} from '../panels/panelLayout';

/** Re-exported so the panel reads every token from one module. */
export {
  rem,
  TILE_SUBTITLE_FONT_SIZE,
  TILE_TITLE_FONT_SIZE,
  TILE_TITLE_FONT_WEIGHT,
};

/**
 * Layout tokens for the AI Cowbell Factors panel.
 *
 * The panel is built on Mantine while the Cowbell Factors panel it shares a
 * `<Paper>` with is MUI, and the two disagreed on both axes of type.
 *
 * Size: Mantine's `rem()` divides by 16 and the app pins its root to 12px
 * (re-based to 14px at 1620 and 16px at 1920), so every Mantine default and
 * every hand-written rem literal in here landed 20-35% under its intended
 * size. Everything now goes through `panelLayout`'s `rem()`, which is built
 * against the 12px root and so re-bases with the text around it.
 *
 * Mantine's `theme.scale` is not the fix for that: it is a single static
 * number, and this app's root font-size is responsive -- 16/12 would correct
 * widths under 1620px and overshoot by a third at 1920px.
 *
 * Typeface: the `@cowbell/ui` theme sets colours only, so Mantine falls back
 * to its stock system stack. Only `Title` is actually affected -- `Text` sets
 * no font-family and inherits the surrounding Roboto on its own -- so the
 * correction is a `font-family` rule on this panel's headings, in the CSS
 * module. Setting `fontFamily` on the theme would move every Mantine surface
 * in the console and wants its own regression pass.
 *
 * It cannot be done by pointing `--mantine-font-family-headings` at `inherit`
 * here: for a custom property `inherit` is the CSS-wide keyword, so it
 * resolves to the parent's value of that same variable -- Mantine's `:root`
 * stack -- rather than to the typeface around the panel.
 */

/**
 * Applied to the panel root, where the CSS module reads them.
 *
 * `--ai-panel-gap` is the column gap and the rule inset beside it. One value
 * because they have to cancel: written independently, the divider stops
 * sitting midway between the two columns.
 */
export const AI_PANEL_STYLE = {
  '--ai-panel-gap': rem(24),
  '--ai-panel-padding': rem(20),
  '--ai-panel-radius': TILE_RADIUS_REM,
  '--ai-panel-row-height': rem(18),
  '--ai-panel-track-height': rem(7),
  '--ai-panel-track-radius': rem(4),
  '--ai-panel-tick-overhang': rem(3),
} as CSSProperties;

/**
 * Applied to the tooltip itself rather than the panel root.
 *
 * Mantine's Tooltip defaults to `withinPortal`, so it mounts at the end of
 * `<body>` and inherits none of the sizes above. Its typeface needs nothing:
 * the label is `Text`, which takes body's Roboto wherever it is mounted.
 */
export const AI_TOOLTIP_STYLE = {
  '--ai-tooltip-padding-block': rem(14),
  '--ai-tooltip-padding-inline': rem(16),
} as CSSProperties;

/**
 * Matches the neighbouring panel's `AggregateSummary`.
 *
 * Also the height of the skeleton that stands in for the number while the
 * scores load. The two have to be one value or the panel jumps as the data
 * lands; they can be, because `.postureIndex` sets the leading to 1, so the
 * number occupies exactly its font-size.
 */
export const POSTURE_INDEX_FONT_SIZE = rem(40);
export const POSTURE_INDEX_FONT_WEIGHT = 600;

/** Matches its "COMPANY AGGREGATE" counterpart. */
export const POSTURE_LABEL_FONT_SIZE = rem(12);
export const POSTURE_LABEL_FONT_WEIGHT = 500;

/** Matches the factor name and score in `FactorRangeRow`. */
export const FACTOR_LABEL_FONT_SIZE = rem(13);
export const FACTOR_LABEL_FONT_WEIGHT = 600;
export const FACTOR_SCORE_FONT_SIZE = rem(17);

/** Matches the row's supporting text and the axis ticks. */
export const FACTOR_STATUS_FONT_SIZE = rem(12);
export const AXIS_TICK_FONT_SIZE = rem(10);

/** Matches the legend line and the panel footer. */
export const LEGEND_FONT_SIZE = rem(11);
export const FOOTER_FONT_SIZE = rem(12.5);
